import moment from "moment";
import { RefObject, useCallback, useEffect } from "react";

import { Week } from "./types";
import { WEEK_COLUMN_WIDTH } from "./utils";

const WEEK_COLUMN_PADDING = 8;

type UseAvailabilityTableScroll = {
  weeks: Week[];
  scrollableContainerRef?: RefObject<HTMLElement | null>;
  isOpen?: boolean;
};

type UseAvailabilityTableScrollReturn = {
  scrollToAvailabilityWeek: (startDate: string | null) => void;
};

export const useAvailabilityTableScroll = ({
  weeks,
  scrollableContainerRef,
  isOpen = true
}: UseAvailabilityTableScroll): UseAvailabilityTableScrollReturn => {
  const scrollToWeekIndex = useCallback(
    (index: number) => {
      if (!scrollableContainerRef?.current) {
        return;
      }

      scrollableContainerRef.current.scrollLeft = index * (WEEK_COLUMN_WIDTH + WEEK_COLUMN_PADDING);
    },
    [scrollableContainerRef]
  );

  const scrollToAvailabilityWeek = useCallback(
    (startDate: string | null) => {
      if (!startDate) {
        return;
      }

      const targetIndex = weeks.findIndex((week) => week.startDate === startDate);
      if (targetIndex !== -1) {
        scrollToWeekIndex(targetIndex);
      }
    },
    [weeks, scrollToWeekIndex]
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const today = moment.utc();
    let targetIndex = weeks.findIndex(
      (week) => !today.isBefore(week.startDate) && !today.isAfter(week.endDate)
    );

    if (targetIndex === -1) {
      targetIndex = today.isBefore(weeks[0]?.startDate ?? today) ? 0 : weeks.length - 1;
    }

    scrollToWeekIndex(targetIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return { scrollToAvailabilityWeek };
};
